/* PhotoLightbox (Phase 114): click any photo to open a large overlay.
   Previous/Next for several photos, zoom in/out (buttons, double-tap/click),
   close button, Escape and arrow keys, swipe on touch devices. Callers pass
   only the photos the viewer is allowed to see. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Minus, Plus, X } from 'lucide-react';
import { fileUrl } from '../lib/api';

export const PhotoLightbox = ({ photos = [], index = 0, open, onClose, onIndexChange, caption = '' }) => {
  const [i, setI] = useState(index);
  const [zoom, setZoom] = useState(1);
  const touchStart = useRef(null);
  const count = photos.length;

  useEffect(() => { setI(index); setZoom(1); }, [index, open]);

  const go = useCallback((delta) => {
    if (count < 2) return;
    const next = (i + delta + count) % count;
    setI(next);
    setZoom(1);
    onIndexChange?.(next);
  }, [i, count, onIndexChange]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(4, z + 0.5));
      if (e.key === '-') setZoom((z) => Math.max(1, z - 0.5));
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, go, onClose]);

  if (!open || count === 0) return null;
  const src = fileUrl(photos[Math.min(i, count - 1)]);

  return (
    <div className="fixed inset-0 z-[1200] bg-black/95 flex flex-col" role="dialog" aria-modal="true" aria-label="Photo viewer" data-testid="photo-lightbox"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="flex items-center justify-between gap-2 p-3 text-white">
        <p className="text-sm font-bold truncate" data-testid="photo-lightbox-counter">{count > 1 ? `Photo ${i + 1} of ${count}` : 'Photo'}{caption ? ` · ${caption}` : ''}</p>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setZoom((z) => Math.max(1, z - 0.5))} disabled={zoom <= 1} className="h-11 w-11 inline-flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-40" aria-label="Zoom out" data-testid="photo-lightbox-zoom-out"><Minus className="h-5 w-5" /></button>
          <span className="text-xs w-10 text-center" data-testid="photo-lightbox-zoom">{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => setZoom((z) => Math.min(4, z + 0.5))} disabled={zoom >= 4} className="h-11 w-11 inline-flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-40" aria-label="Zoom in" data-testid="photo-lightbox-zoom-in"><Plus className="h-5 w-5" /></button>
          <button type="button" onClick={onClose} className="h-11 w-11 inline-flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 ml-1" aria-label="Close photo viewer" data-testid="photo-lightbox-close"><X className="h-6 w-6" /></button>
        </div>
      </div>
      <div className="relative flex-1 min-h-0 overflow-auto flex items-center justify-center"
        onTouchStart={(e) => { touchStart.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => { if (touchStart.current == null || zoom > 1) return; const dx = e.changedTouches[0].clientX - touchStart.current; if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1); touchStart.current = null; }}>
        {count > 1 && (
          <button type="button" onClick={() => go(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-12 w-12 inline-flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white" aria-label="Previous photo" data-testid="photo-lightbox-prev"><ChevronLeft className="h-7 w-7" /></button>
        )}
        <img
          src={src}
          alt={`${caption || 'Attached photo'} ${i + 1}`}
          onDoubleClick={() => setZoom((z) => (z > 1 ? 1 : 2))}
          className="select-none max-h-full max-w-full object-contain transition-transform duration-150"
          style={{ transform: `scale(${zoom})`, transformOrigin: 'center center', cursor: zoom > 1 ? 'zoom-out' : 'zoom-in' }}
          draggable={false}
          data-testid="photo-lightbox-image"
        />
        {count > 1 && (
          <button type="button" onClick={() => go(1)} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-12 w-12 inline-flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white" aria-label="Next photo" data-testid="photo-lightbox-next"><ChevronRight className="h-7 w-7" /></button>
        )}
      </div>
      {count > 1 && (
        <div className="flex justify-center gap-2 p-3 overflow-x-auto" data-testid="photo-lightbox-thumbs">
          {photos.map((p, k) => (
            <button key={p} type="button" onClick={() => { setI(k); setZoom(1); onIndexChange?.(k); }} className={`h-14 w-14 shrink-0 rounded-md overflow-hidden border-2 ${k === i ? 'border-[#C9A84C]' : 'border-transparent opacity-70'}`} aria-label={`Show photo ${k + 1}`} data-testid={`photo-lightbox-thumb-${k}`}>
              <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* Grid of clickable thumbnails wired to the lightbox. */
export const PhotoGrid = ({ photos = [], caption = '', testId = 'photo-grid', className = '', badge }) => {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  if (!photos.length) return null;
  return (
    <>
      <div className={`grid grid-cols-2 sm:grid-cols-3 gap-2 ${className}`} data-testid={testId}>
        {photos.map((p, i) => (
          <button key={p} type="button" onClick={() => { setIdx(i); setOpen(true); }} className="relative group rounded-lg overflow-hidden border border-slate-200 focus-visible:ring-2 focus-visible:ring-[#C9A84C]" aria-label={`Open photo ${i + 1} larger`} data-testid={`${testId}-item-${i}`}>
            <img src={fileUrl(p)} alt={`${caption || 'Photo'} ${i + 1}`} className="object-cover w-full h-32 group-hover:scale-[1.02] transition-transform duration-150" loading="lazy" />
            <span className="absolute bottom-1 right-1 text-[10px] font-bold bg-black/60 text-white rounded px-1.5 py-0.5">Tap to enlarge</span>
            {badge ? badge(p, i) : null}
          </button>
        ))}
      </div>
      <PhotoLightbox photos={photos} index={idx} open={open} onClose={() => setOpen(false)} onIndexChange={setIdx} caption={caption} />
    </>
  );
};
